/**
 * Shared recalc core: single implementation parameterized by config (formulas, fieldDependencies, getBlendedWhlsFee).
 * NC/Direct/Indirect template logics call recalculateFieldsCore with their own config.
 */

import type { RecalculateFieldsParams } from './shared/logic';
import { bidAnalysisStore } from '../../../stores/bidAnalysisStore';

/** Context passed to getBlendedWhlsFee for NC/Direct blended fee calculation. */
export interface BlendedWhlsFeeContext {
  updatedData: Record<string, any>;
  newData: Record<string, any>;
  templateData?: any;
  suggestedBidPrice: number;
  vaAnnualNcEst: number;
  dodAnnualNcEst: number;
  ihsAnnualNcEst: number;
  bopAnnualNcEst: number;
  usagePeriodStartDate?: string;
  yearIndex?: number;
  useOptionYearDateForNcWhlsFee: boolean;
  isNC: boolean;
  isDirect: boolean;
}

/** Config for recalculateFieldsCore: formulas bag, field dependencies, blended fee calculator. */
export interface RecalcConfig {
  formulas: Record<string, (...args: any[]) => any>;
  fieldDependencies: Record<string, string[]>;
  getBlendedWhlsFee: (ctx: BlendedWhlsFeeContext) => number;
}

/**
 * Recalculate dependent fields using config-provided formulas and dependencies.
 */
export function recalculateFieldsCore(params: RecalculateFieldsParams, config: RecalcConfig): Record<string, any> {
  const F = config.formulas;
  const FIELD_DEPENDENCIES = config.fieldDependencies;
  const getBlendedWhlsFee = config.getBlendedWhlsFee;

  const { updatedData, changedField, productType, templateData, analysisType, contractPriceRanking, templateType, supplierName, yearIndex } = params;
  const isDirect = productType === 'direct';
  const isIndirect = productType === 'indirect';
  const isNC = templateType === 'nc';
  const newData = { ...updatedData };
  
  
  
  // Extract lowest contract price from contract price ranking data
  // Rank 1 is the lowest price
  let lowestContractPriceFromRanking = 0;
  if (contractPriceRanking && contractPriceRanking.length > 0) {
    const lowestRankItem = contractPriceRanking.find(item => item.price_rank === 1);
    if (lowestRankItem && lowestRankItem.price) {
      lowestContractPriceFromRanking = typeof lowestRankItem.price === 'number' 
        ? lowestRankItem.price 
        : parseFloat(lowestRankItem.price) || 0;
    }
  }
  
  // Use the lowest contract price from ranking if available, otherwise use the value from form data
  const lowestContractPrice = lowestContractPriceFromRanking > 0 
    ? lowestContractPriceFromRanking 
    : (parseFloat(newData.lowestContractPrice) || 0);
  
  // Update newData with the extracted lowest contract price
  if (lowestContractPriceFromRanking > 0) {
    newData.lowestContractPrice = lowestContractPriceFromRanking.toFixed(2);
  }

  // Check if we should recalculate (either no changedField specified, or field has dependencies)
  const shouldRecalculate = !changedField || FIELD_DEPENDENCIES[changedField];

  if (!shouldRecalculate) {
    return newData;
  }

  // Track which fields are being recalculated
  const recalculatedFields: string[] = [];

  // Special handling for manually edited calculated fields
  // If user manually edits GSMS Net Cost From Mfg, calculate backwards to GSMS Cost From Mfg
  if (changedField === 'gsmsNetCostFromMfg') {
    const netCost = parseFloat(newData.gsmsNetCostFromMfg) || 0;
    
    if (isIndirect) {
      // For indirect products, Net Cost = Cost From Mfg (no discount)
      newData.gsmsCostFromMfg = netCost.toFixed(2);
      recalculatedFields.push(`gsmsCostFromMfg (Formula: Cost From Mfg = Net Cost From Mfg for Indirect)`);
    } else {
      // For direct products, use the actual supplier discount if available
      const supplierDiscount = newData.supplierDiscount;
      if (supplierDiscount !== undefined && supplierDiscount !== null && supplierDiscount > 0) {
        // Reverse formula: if Net Cost = Cost From Mfg * (1 - discount), then Cost From Mfg = Net Cost / (1 - discount)
        const calculatedCostFromMfg = netCost / (1 - supplierDiscount);
        newData.gsmsCostFromMfg = calculatedCostFromMfg.toFixed(2);
        const discountPercent = (supplierDiscount * 100).toFixed(1);
        recalculatedFields.push(`gsmsCostFromMfg (Formula: Cost From Mfg = Net Cost / (1 - ${discountPercent}%))`);
      } else {
        // No discount available, so Net Cost = Cost From Mfg
        newData.gsmsCostFromMfg = netCost.toFixed(2);
        recalculatedFields.push(`gsmsCostFromMfg (Formula: Cost From Mfg = Net Cost From Mfg (no discount))`);
      }
    }
  }

  // If user manually edits GSMS IFF Fee, calculate backwards to Suggested BID Price
  if (changedField === 'gsmsIffFee') {
    const iffFee = parseFloat(newData.gsmsIffFee) || 0;
    // Reverse formula: if IFF Fee = Suggested BID Price * 0.005, then Suggested BID Price = IFF Fee / 0.005
    const calculatedBidPrice = iffFee / 0.005;
    newData.suggestedBidPrice = calculatedBidPrice.toFixed(2);
    recalculatedFields.push(`suggestedBidPrice (Formula: Suggested BID Price = IFF Fee / 0.005)`);
  }

  // Parse numeric values (these will be used throughout, but some may be updated by goal seek)
  // IMPORTANT: When suggestedBidPrice changes OR when gsmsCostFromMfg is blurred without change,
  // preserve gsmsCostFromMfg from updatedData to ensure consistency
  // This prevents the value from changing when clicking outside the field, which would cause GSMS Profit to change unexpectedly
  let gsmsCostFromMfg: number;
  const updatedGsmsCostFromMfg = updatedData.gsmsCostFromMfg !== undefined && updatedData.gsmsCostFromMfg !== null && updatedData.gsmsCostFromMfg !== '' 
    ? parseFloat(String(updatedData.gsmsCostFromMfg)) 
    : null;
  const newDataGsmsCostFromMfg = newData.gsmsCostFromMfg !== undefined && newData.gsmsCostFromMfg !== null && newData.gsmsCostFromMfg !== ''
    ? parseFloat(String(newData.gsmsCostFromMfg))
    : null;
  
  // Preserve gsmsCostFromMfg in these cases:
  // 1. When suggestedBidPrice changes (we'll check goal seek later)
  // 2. When gsmsCostFromMfg is blurred but the value hasn't actually changed (user just clicked outside)
  // Note: goalSeekTriggered is checked later, but we'll handle it when we re-read after goal seek
  const shouldPreserveGsmsCostFromMfg = updatedGsmsCostFromMfg !== null && 
      ((changedField === 'suggestedBidPrice') || 
       (changedField === 'gsmsCostFromMfg' && updatedGsmsCostFromMfg === newDataGsmsCostFromMfg));
  
  if (shouldPreserveGsmsCostFromMfg) {
    // Preserve the existing value to maintain consistency
    gsmsCostFromMfg = updatedGsmsCostFromMfg;
    // Ensure newData also has this value for consistency
    newData.gsmsCostFromMfg = String(updatedData.gsmsCostFromMfg);
  } else {
    // Normal case: use value from newData (includes when user actually changes gsmsCostFromMfg)
    gsmsCostFromMfg = newDataGsmsCostFromMfg !== null ? newDataGsmsCostFromMfg : 0;
  }
  // GSMS Pack Size may be in gsmsPackSize or packSize (from pack size tab); use both so bottle capacity can be calculated
  const packSize = parseFloat(newData.gsmsPackSize) || parseFloat(newData.packSize) || 0;
  const handlingType = newData.gsmsHndlgType || '';

 
  const description = newData.description || templateData?.productDetails?.description || '';
  const suggestedBidPrice = parseFloat(newData.suggestedBidPrice) || 0;
  const vaUsage = parseFloat(newData.vaAnnualUsage) || 0;
  const dodUsage = parseFloat(newData.dodAnnualUsage) || 0;
  const vaTms = parseFloat(newData.vaTmsPercent) || 0;
  const dodTms = parseFloat(newData.dodTmsPercent) || 0;
  // NC-specific usage fields
  const vaAnnualNcEst = parseFloat(newData.vaAnnualNcEst) || 0;
  const dodAnnualNcEst = parseFloat(newData.dodAnnualNcEst) || 0;
  const ihsAnnualNcEst = parseFloat(newData.ihsAnnualNcEst) || 0;
  const bopAnnualNcEst = parseFloat(newData.bopAnnualNcEst) || 0;
  let wac = parseFloat(newData.wac) || 0;
  const tmopPercent = parseFloat(newData.tmopPercent) || 0;
  const mfgBottleSize = parseFloat(newData.mfgBottleSize) || 0;
  
  // Auto-calculate WAC for Direct products when Suggested Bid Price changes
  if (isDirect && changedField === 'suggestedBidPrice' && suggestedBidPrice > 0) {
    wac = F.calculateWACForDirectBid(suggestedBidPrice);
    newData.wac = wac.toFixed(2);
  } else if (isDirect && !wac && suggestedBidPrice > 0) {
    // If WAC is empty/0 and we have a suggested bid price, calculate it
    wac = F.calculateWACForDirectBid(suggestedBidPrice);
    newData.wac = wac.toFixed(2);
  }
  
  // Auto-calculate WAC for NC templates when Suggested Bid Price changes
  // Formula: WAC = Suggested Bid Price + 0.15
  if (isNC && changedField === 'suggestedBidPrice' && suggestedBidPrice > 0) {
    wac = F.calculateWACForNC(suggestedBidPrice);
    newData.wac = wac.toFixed(2);
  } else if (isNC && !wac && suggestedBidPrice > 0) {
    // If WAC is empty/0 and we have a suggested bid price, calculate it for NC
    wac = F.calculateWACForNC(suggestedBidPrice);
    newData.wac = wac.toFixed(2);
  }
  
  // Parse GSMS Profit - handle negative values correctly
  // IMPORTANT: Check updatedData first if changedField is 'gsmsProfit' to get the manual value
  let gsmsProfitInput: number = NaN;
  if (changedField === 'gsmsProfit' && updatedData.gsmsProfit !== null && updatedData.gsmsProfit !== undefined) {
    // When gsmsProfit is manually changed, use the value from updatedData
    if (typeof updatedData.gsmsProfit === 'number') {
      gsmsProfitInput = updatedData.gsmsProfit;
    } else if (typeof updatedData.gsmsProfit === 'string') {
      const parsed = parseFloat(updatedData.gsmsProfit);
      gsmsProfitInput = isNaN(parsed) ? NaN : parsed;
    }
  } else if (newData.gsmsProfit !== null && newData.gsmsProfit !== undefined) {
    if (typeof newData.gsmsProfit === 'number') {
      gsmsProfitInput = newData.gsmsProfit;
    } else if (typeof newData.gsmsProfit === 'string') {
      const parsed = parseFloat(newData.gsmsProfit);
      gsmsProfitInput = isNaN(parsed) ? NaN : parsed;
    }
  }

  // Helper function to safely format numbers, returning '0.00' if NaN/null/undefined (to prevent NaN display)
  const safeToFixed = (value: number, decimals: number): string => {
    if (isNaN(value) || value === null || value === undefined) {
      // Return '0.00' for 2 decimals, '0.0' for 1 decimal, etc.
      return '0.' + '0'.repeat(decimals);
    }
    return value.toFixed(decimals);
  };

  // COGS fields that can come from API - preserve API values unless dependencies change
  // Dependencies that trigger COGS recalculation: gsmsHndlgType, gsmsPackSize, mfgBottleSize, gsmsBottleCapacity
  // NOTE: GSMS Bottle Capacity does NOT depend on MFG Bottle Size. It only depends on gsmsHndlgType + pack size.
  // We show/populate bottle capacity as soon as handling type and pack size are available (no need to wait for MFG Bottle Size).
  // PV2Percent also depends on WAC, so recalculate it if WAC changes
  const cogsDependencyFields = ['gsmsHndlgType', 'gsmsPackSize', 'mfgBottleSize', 'gsmsBottleCapacity'];
  
  // Check if COGS fields exist in API data - preserve them if they do
  // Only recalculate if dependencies changed OR if COGS fields don't exist from API
  const hasApiBottleCapacity = newData.gsmsBottleCapacity !== null && 
                                newData.gsmsBottleCapacity !== undefined && 
                                newData.gsmsBottleCapacity !== '' &&
                                newData.gsmsBottleCapacity !== ' ';
  
  // Check if any COGS cost fields exist from API
  const hasApiCogsCosts = 
    (newData.gsmsBottleCost !== null && newData.gsmsBottleCost !== undefined && newData.gsmsBottleCost !== '' && !isNaN(parseFloat(newData.gsmsBottleCost))) ||
    (newData.gsmsCapCost !== null && newData.gsmsCapCost !== undefined && newData.gsmsCapCost !== '' && !isNaN(parseFloat(newData.gsmsCapCost))) ||
    (newData.gsmsLabelCost !== null && newData.gsmsLabelCost !== undefined && newData.gsmsLabelCost !== '' && !isNaN(parseFloat(newData.gsmsLabelCost)));
  
  // Only recalculate if:
  // 1. A dependency field changed (gsmsHndlgType, gsmsPackSize, etc.) OR
  // 2. No API values exist (initial empty state)
  // On initial load (changedField is undefined), preserve API values if they exist
  const shouldRecalculateCogs = changedField ? cogsDependencyFields.includes(changedField) : (!hasApiBottleCapacity && !hasApiCogsCosts);
  
  // PV2% should recalculate when:
  // 1. COGS fields change
  // 2. WAC is directly changed
  // 3. suggestedBidPrice changes (for Direct/NC, this updates WAC which should update PV2%)
  const shouldRecalculatePv2Percent = shouldRecalculateCogs || 
                                       changedField === 'wac' || 
                                       (changedField === 'suggestedBidPrice' && (isDirect || isNC));
  
  let bottleCapacity: string;
  // Pack size for bottle capacity: use gsmsPackSize or packSize (same value from pack size tab, different keys)
  // Bottle capacity formula uses ONLY handling type + pack size (NOT mfgBottleSize).
  const currentPackSizeForBottleCapacity = parseFloat(newData.gsmsPackSize) || parseFloat(newData.packSize) || packSize;
  // For direct/NC we show bottle capacity as soon as we have handling type + pack size (no dependency on MFG Bottle Size)
  const hasInputsForBottleCapacity = (isDirect || isNC) && currentPackSizeForBottleCapacity > 0 && !!handlingType;
  const bottleCapacityEmpty = !newData.gsmsBottleCapacity || String(newData.gsmsBottleCapacity).trim() === '';
  const shouldAlwaysShowBottleCapacity = hasInputsForBottleCapacity && bottleCapacityEmpty;
  // Force recalculation if handling type changed, even if API value exists (but is blank/empty)
  const shouldForceRecalculate = changedField === 'gsmsHndlgType' && (!hasApiBottleCapacity || bottleCapacityEmpty);

  if ((isDirect || isNC) && (shouldRecalculateCogs || !hasApiBottleCapacity || shouldForceRecalculate || shouldAlwaysShowBottleCapacity)) {
    // Calculate bottle capacity (dependent on pack size and handling type) — only for Direct/NC; Indirect has no bottle capacity formula
    bottleCapacity = isNC
      ? F.calculateNCGSMSBottleCapacity(handlingType, currentPackSizeForBottleCapacity)
      : F.calculateGSMSBottleCapacity(handlingType, currentPackSizeForBottleCapacity);

    newData.gsmsBottleCapacity = bottleCapacity || '';
   } else {
    // Preserve API value for bottle capacity only when it has a value; for Indirect, leave empty
    bottleCapacity = newData.gsmsBottleCapacity ?? '';
   }
  
  // Declare COGS variables outside the if block so they're available for later calculations
  // Initialize with default values from newData if they exist, otherwise 0
  let bottleCost: number = parseFloat(newData.gsmsBottleCost) || 0;
  let capCost: number = parseFloat(newData.gsmsCapCost) || 0;
  let labelCost: number = parseFloat(newData.gsmsLabelCost) || 0;
  let insertCost: number = parseFloat(newData.gsmsInsertCost) || 0;
  let boxCartonCost: number = parseFloat(newData.gsmsBoxCartonCost) || 0;
  let outsourceRepackFee: number = parseFloat(newData.outsourceRepackFee) || 0;
  let frtCost: number = parseFloat(newData.gsmsFrtCost) || 0;
  let pv2Percent: number = parseFloat(newData.pv2Percent) || 0;
  let labour: number = parseFloat(newData.gsmsLabor) || 0;
  
  // Only recalculate COGS fields if dependencies changed (Direct/NC only; Indirect has no COGS buildup)
  // This preserves API values unless the user changes handling type, pack size, etc.
  // Similar to how gsmsCostFromMfg is preserved from API
  if ((isDirect || isNC) && shouldRecalculateCogs) {
    
    
    // Calculate bottle cost (dependent on handling type and bottle capacity)
    // For NC template, use NC-specific formula; for Direct/MAS, use Direct formula
    bottleCost = isNC
      ? F.calculateNCGSMSBottleCost(handlingType, bottleCapacity)
      : F.calculateGSMSBottleCost(handlingType, bottleCapacity);
    newData.gsmsBottleCost = safeToFixed(bottleCost, 3);
   
    // Calculate cap cost (dependent on bottle cost and capacity)
    capCost = F.calculateGSMSCapCost(bottleCost, bottleCapacity);
    newData.gsmsCapCost = safeToFixed(capCost, 3);

    // Calculate label cost (dependent on handling type and bottle cost)
    labelCost = F.calculateGSMSLabelCost(handlingType, bottleCost);
    newData.gsmsLabelCost = safeToFixed(labelCost, 3);

    // Calculate insert cost (dependent on bottle cost and description/supplier name)
    // For NC template, use supplier name; for Direct/MAS, use description
    insertCost = isNC && supplierName
      ? F.calculateNCGSMSInsertCost(bottleCost, supplierName)
      : F.calculateGSMSInsertCost(bottleCost, description);
    newData.gsmsInsertCost = safeToFixed(insertCost, 3);

    // Calculate box/carton cost (dependent on bottle cost and capacity)
    boxCartonCost = F.calculateGSMSBoxCartonCost(bottleCost, bottleCapacity);
    newData.gsmsBoxCartonCost = safeToFixed(boxCartonCost, 3);

    // Calculate outsource repack fee (dependent on handling type)
    outsourceRepackFee = isNC ? F.calculateOutsourceRepackFee(handlingType) : F.calculateOutsourceRepackFee(handlingType);
    newData.outsourceRepackFee = safeToFixed(outsourceRepackFee, 2);

    // Calculate FRT cost (dependent on bottle capacity and outsource fee)
    // For NC template, use NC-specific rates (0.1 for 120cc, 0.25 for others)
    // For Direct/MAS template, use Direct rates (0.1059 for 120cc, 0.26475 for others)
    frtCost = isNC
      ? F.calculateNCGSMSFrtCost(bottleCapacity, outsourceRepackFee)
      : F.calculateGSMSFrtCost(bottleCapacity, outsourceRepackFee);
    newData.gsmsFrtCost = safeToFixed(frtCost, 5);

    // Calculate PV 2% (dependent on bottle capacity and WAC)
    // Always recalculate PV2Percent if WAC changed, even if other COGS values are preserved
    if (shouldRecalculatePv2Percent) {
      pv2Percent = F.calculatePV2Percent(bottleCapacity, wac);
      newData.pv2Percent = safeToFixed(pv2Percent, 2);
    }

    // Calculate labour (dependent on bottle capacity, bottle cost, and outsource repack fee for NC)
    // For NC template, use NC-specific formula based on bottle capacity, bottle cost, and outsource repack fee
    // For Direct/MAS template, use handling type and suggested bid price based formula
    labour = isNC
      ? F.calculateNCGSMSLabor(bottleCapacity, bottleCost, outsourceRepackFee)
      : F.calculateGSMSLabor(handlingType, suggestedBidPrice);
    newData.gsmsLabor = safeToFixed(labour, 5);
  } else {
    // Preserve API values - don't recalculate COGS fields
    // Keep existing values from newData (which came from API)
    // Variables are already initialized with values from newData above
    
    // Exception: PV2Percent should still be recalculated if WAC changed (Direct/NC only; Indirect has no PV2)
    if ((isDirect || isNC) && shouldRecalculatePv2Percent) {
      pv2Percent = F.calculatePV2Percent(bottleCapacity, wac);
      newData.pv2Percent = safeToFixed(pv2Percent, 2);
       
    }
    // Otherwise, pv2Percent already has the value from newData (initialized above)
  }

  // Calculate IFF Fee (dependent on Suggested BID Price)
  // Note: When Suggested BID Price changes, IFF Fee must be recalculated, which will trigger GSMS Cost and GSMS Profit recalculation
  const iffFee = F.calculateGSMSIffFee(suggestedBidPrice);
  newData.gsmsIffFee = safeToFixed(iffFee, 2);
  if (changedField === 'suggestedBidPrice' || (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsIffFee'))) {
    recalculatedFields.push(`gsmsIffFee (Formula: IFF Fee = Suggested BID Price × 0.005)`);
  }

  // CRITICAL: Calculate Blended WHLS Fee BEFORE goal seek and GSMS Cost
  // Goal seek uses blendedWhlsFee in additionalCosts; GSMS Cost uses it in the formula
  // If we calculate it later, both would use the OLD value (one step behind)
  // When NC option year + gsmsCostFromMfg, force recalc so option-year date is used (copy-for-all-years).
  // Exception: when the caller signals _skipBlendedWhlsFeeRecalc (user blurred without changing the
  // value), do NOT force-recalculate blendedWhlsFee — the correct per-year fee set by copyForAllYears
  // must not be overwritten by a no-op blur on gsmsCostFromMfg.
  const forceBlendedWhlsFeeForNcYear =
    isNC &&
    yearIndex !== undefined &&
    yearIndex !== null &&
    yearIndex > 0 &&
    changedField === 'gsmsCostFromMfg' &&
    !updatedData._skipBlendedWhlsFeeRecalc;
  // When NC and yearIndex > 0, always use option-year date for blendedWhlsFee so no recalc overwrites with base-year date (0.00).
  const useOptionYearDateForNcWhlsFee =
    isNC && yearIndex !== undefined && yearIndex !== null && yearIndex > 0;
  const shouldRecalculateBlendedWhlsFee =
    (forceBlendedWhlsFeeForNcYear ||
      !changedField ||
      changedField === 'targetLaunchDate' ||
      changedField === 'usagePeriodStartDate' ||
      changedField === 'usagePeriodEndDate' ||
      changedField === 'suggestedBidPrice' ||
      changedField === 'vaAnnualUsage' ||
      changedField === 'dodAnnualUsage' ||
      changedField === 'vaTmsPercent' ||
      changedField === 'dodTmsPercent' ||
      changedField === 'vaAnnualNcEst' ||
      changedField === 'dodAnnualNcEst' ||
      changedField === 'ihsAnnualNcEst' ||
      changedField === 'bopAnnualNcEst' ||
      (changedField && FIELD_DEPENDENCIES[changedField]?.includes('blendedWhlsFee'))) &&
    (isDirect || isNC);

  if (shouldRecalculateBlendedWhlsFee) {
    const blendedWhlsFeeCtx: BlendedWhlsFeeContext = {
      updatedData,
      newData,
      templateData,
      suggestedBidPrice,
      vaAnnualNcEst,
      dodAnnualNcEst,
      ihsAnnualNcEst,
      bopAnnualNcEst,
      usagePeriodStartDate: newData.usagePeriodStartDate || templateData?.usagePeriodStartDate,
      yearIndex,
      useOptionYearDateForNcWhlsFee,
      isNC,
      isDirect,
    };
    const blendedWhlsFee = getBlendedWhlsFee(blendedWhlsFeeCtx);

    const previousBlendedWhlsFee = newData.blendedWhlsFee || updatedData.blendedWhlsFee || 'N/A';
    newData.blendedWhlsFee = safeToFixed(blendedWhlsFee, 2);

    const blendedWhlsFeeChanged =
      previousBlendedWhlsFee !== newData.blendedWhlsFee && previousBlendedWhlsFee !== 'N/A';

    if (blendedWhlsFeeChanged) {
      (newData as any)._blendedWhlsFeeChanged = true;
    }

    if (
      changedField === 'targetLaunchDate' ||
      changedField === 'usagePeriodStartDate' ||
      changedField === 'usagePeriodEndDate' ||
      changedField === 'suggestedBidPrice' ||
      changedField === 'vaAnnualUsage' ||
      changedField === 'dodAnnualUsage' ||
      changedField === 'vaTmsPercent' ||
      changedField === 'dodTmsPercent' ||
      changedField === 'vaAnnualNcEst' ||
      changedField === 'dodAnnualNcEst' ||
      changedField === 'ihsAnnualNcEst' ||
      changedField === 'bopAnnualNcEst' ||
      (changedField && FIELD_DEPENDENCIES[changedField]?.includes('blendedWhlsFee'))
    ) {
      recalculatedFields.push(
        `blendedWhlsFee (Formula: Blended WHLS Fee = Suggested Bid Price × Weighted Average Rate)`
      );
    }
  }

  // GOAL SEEK: When user manually changes GSMS Profit OR when suggestedBidPrice changes
  // CRITICAL: Three-way binding ensures:
  // 1. When gsmsCostFromMfg changes → gsmsProfit updates (two-way binding)
  // 2. When suggestedBidPrice changes → gsmsCostFromMfg updates (via goal seek)
  // 3. When gsmsProfit changes → gsmsCostFromMfg updates (via goal seek)
  // Flow: GSMS Profit → Target GSMS Cost → Target Net Cost From Mfg → Target Cost From Mfg
  // Then let normal recalculation flow update dependent variables: Cost From Mfg → Net Cost From Mfg → GSMS Cost → GSMS Profit
  // Initialize gsmsCost variable - will be set in calculation flow below
  let gsmsCost: number = 0;
  let goalSeekTriggered = false;
  
  // Check if GSMS Profit was manually set (from previous manual changes) - for goal seek logic
  const _isProfitManuallySetForGoalSeek = updatedData._gsmsProfitManuallySet === true || updatedData.gsmsProfitManuallySet === true;
  
  // For Direct and NC: When suggestedBidPrice changes, use the current profit for goal seek
  // If profit was manually set, use that value. Otherwise, use the current calculated profit.
  // CRITICAL: Always use current profit (whether manually set or calculated) when suggestedBidPrice changes
  // This ensures gsmsCostFromMfg is updated whenever suggestedBidPrice changes
  let profitForGoalSeek: number | null = null;
  if ((isDirect || isNC || isIndirect) && changedField === 'suggestedBidPrice' && suggestedBidPrice > 0) {
    // Get the current profit value (either manually set or calculated)
    const currentProfitValue = parseFloat(updatedData.gsmsProfit) || 0;
    // Use current profit value if it's valid, even if it's 0 (calculated value)
    // Only exclude if it's NaN or outside valid range
    if (!isNaN(currentProfitValue) && currentProfitValue >= -100 && currentProfitValue <= 100) {
      profitForGoalSeek = currentProfitValue;
    }
  }

  // Trigger goal seek when:
  // 1. User manually changes GSMS Profit, OR
  // 2. For Direct/NC/Indirect: suggestedBidPrice changes (ALWAYS update gsmsCostFromMfg using current profit)
  // CRITICAL: When suggestedBidPrice changes, ALWAYS trigger goal seek to update gsmsCostFromMfg
  // This ensures gsmsCostFromMfg is updated whenever suggestedBidPrice changes
  // Use current profit value (manually set or calculated) if available, otherwise use 0 as default
  // Allow 100 to trigger goal seek (when user enters value > 100, it gets capped to 100)
  // For Price Change, allow negative values (-100 to 100)
  // NOTE: Goal seek requires suggested bid price > 0 to calculate target cost
  // If suggested bid price is not set, we just store the value and goal seek will trigger when suggested bid price is set
  // IMPORTANT: The check for whether value actually changed is now done in handleFieldChange
  // If handleFieldChange is called, it means the value actually changed, so we can safely trigger goal seek
  // CRITICAL: Goal seek ALWAYS runs when gsmsProfit% changes, even if gsmsCostFromMfg was manually set
  // This ensures that when user changes profit%, gsmsCostFromMfg is updated accordingly
  const shouldTriggerGoalSeek = 
    (changedField === 'gsmsProfit' &&
    !isNaN(gsmsProfitInput) && 
    gsmsProfitInput >= -100 && 
    gsmsProfitInput <= 100 && 
    suggestedBidPrice > 0) ||
    ((isDirect || isNC || isIndirect) && changedField === 'suggestedBidPrice' && suggestedBidPrice > 0);
  
  if (shouldTriggerGoalSeek) {
    goalSeekTriggered = true;
     // Step 1: Calculate target GSMS Cost from desired GSMS Profit
    // Formula: GSMS Profit = (Suggested Bid Price - GSMS Cost) / Suggested Bid Price
    // Rearranging: GSMS Cost = Suggested Bid Price * (1 - GSMS Profit / 100)
    // Use gsmsProfitInput if manually changed, otherwise use profitForGoalSeek (for Direct/NC when suggestedBidPrice changes)
    // When suggestedBidPrice changes, profitForGoalSeek should contain the current profit value to maintain
    // CRITICAL: Always use current profit when suggestedBidPrice changes to ensure gsmsCostFromMfg updates
    // If profitForGoalSeek is null (no profit value yet), use 0 as default
    const profitToUse = changedField === 'gsmsProfit' ? gsmsProfitInput : (profitForGoalSeek ?? 0);
    const targetGsmsCost = suggestedBidPrice * (1 - profitToUse / 100);
    const goalSeekReason = changedField === 'gsmsProfit' 
      ? 'GSMS Profit manually changed'
      : 'Suggested Bid Price changed (maintaining manually set GSMS Profit)';
    
    // IMPORTANT: Goal seek will update gsmsCostFromMfg, which triggers recalculation of
    // gsmsNetCostFromMfg and then gsmsCost in the normal flow below
    
    if (isDirect || isNC) {
      // Step 2: For Direct, calculate what Net Cost From Mfg should be
      // GSMS Cost = (Net Cost From Mfg / mfgBottleSize) * gsmsPackSize + additionalCosts
      // First, calculate all additional costs (non-Net-Cost components) directly
      const additionalCosts = 
        bottleCost +
        capCost +
        labelCost +
        insertCost +
        iffFee +
        boxCartonCost +
        (parseFloat(newData.gsmsDesiccantCost) || 0) +
        outsourceRepackFee +
        (parseFloat(newData.blendedWhlsFee) || 0) +
        frtCost +
        pv2Percent +
        labour;
      
      // Step 3: Calculate target Net Cost From Mfg
      // Formula: targetGsmsCost = (targetNetCostFromMfg / mfgBottleSize) * gsmsPackSize + additionalCosts
      // Rearranging: 
      //   targetGsmsCost - additionalCosts = (targetNetCostFromMfg / mfgBottleSize) * gsmsPackSize
      //   (targetGsmsCost - additionalCosts) / gsmsPackSize = targetNetCostFromMfg / mfgBottleSize
      //   targetNetCostFromMfg = (targetGsmsCost - additionalCosts) / gsmsPackSize * mfgBottleSize
      const targetNetCostFromMfgScaled = targetGsmsCost - additionalCosts;
      
      // Both packSize and mfgBottleSize must be valid for the calculation
      // No assumptions - if either is missing or invalid, cannot calculate
      let targetNetCostFromMfg: number;
      if (packSize > 0 && mfgBottleSize > 0) {
        // Normal case: use the formula
        targetNetCostFromMfg = (targetNetCostFromMfgScaled / packSize) * mfgBottleSize;
      } else {
        // Invalid data - cannot calculate without both values
        // Return NaN to indicate invalid calculation
        targetNetCostFromMfg = NaN;
      }
      
      // Step 4: Work backwards to find target Cost From Mfg
      // For Direct: Net Cost From Mfg = Cost From Mfg * (1 - supplierDiscount)
      // So: Cost From Mfg = Net Cost From Mfg / (1 - supplierDiscount)
      // This must match the behavior of calculateGSMSNetCostFromMfg formula:
      // - If supplierDiscount is provided (not null/undefined), use it
      // - If supplierDiscount is null/undefined, return original cost (no discount, multiplier = 1.0)
      const supplierDiscount = newData.supplierDiscount;
      // Handle supplier discount to match calculateGSMSNetCostFromMfg behavior:
      // - If supplierDiscount is explicitly 0, it means no discount (multiplier = 1.0)
      // - If supplierDiscount is null/undefined, no discount (multiplier = 1.0) - matches formula behavior
      // - If supplierDiscount has a value > 0, use it (multiplier = 1 - discount)
      let discountMultiplier: number;
      let actualDiscountUsed: number; // Track the actual discount used for logging
      if (supplierDiscount === 0 || supplierDiscount === undefined || supplierDiscount === null) {
        // No discount: 0, null, or undefined all mean no discount (multiplier = 1.0)
        // This matches calculateGSMSNetCostFromMfg which returns original cost when discount is not provided
        discountMultiplier = 1.0;
        actualDiscountUsed = 0; // No discount was applied
      } else if (supplierDiscount > 0) {
        // Has a discount value > 0, calculate multiplier
        const calculatedMultiplier = 1 - supplierDiscount;
        // Clamp to valid range to prevent division by zero and invalid values
        discountMultiplier = Math.max(0.01, Math.min(1.0, calculatedMultiplier));
        // Track the actual discount that was used (may be clamped)
        actualDiscountUsed = supplierDiscount;
      } else {
        // Negative discount (shouldn't happen, but handle gracefully)
        discountMultiplier = 1.0;
        actualDiscountUsed = 0; // No discount applied for invalid negative discount
      }
      
      // Calculate target Cost From Mfg
      // Only calculate if targetNetCostFromMfg is valid (not NaN)
      let targetCostFromMfg: number;
      if (isNaN(targetNetCostFromMfg)) {
        // Cannot calculate if targetNetCostFromMfg is invalid
        targetCostFromMfg = NaN;
      } else if (discountMultiplier > 0) {
        // Use discount multiplier if available (handles 0, positive, and negative values)
        targetCostFromMfg = targetNetCostFromMfg / discountMultiplier;
      } else {
        // Fallback if discount multiplier is invalid - use targetNetCostFromMfg as-is
        targetCostFromMfg = targetNetCostFromMfg;
      }
      
      // Set the result - allow 0, negative values if calculation produces them
      // If invalid (NaN), set to empty string or handle appropriately
      // CRITICAL: Always update gsmsCostFromMfg when goal seek runs, even if it was manually set before
      // This ensures that when user changes gsmsProfit%, gsmsCostFromMfg is updated accordingly
      const _previousGsmsCostFromMfg = newData.gsmsCostFromMfg || updatedData.gsmsCostFromMfg || 'N/A';
      
      if (isNaN(targetCostFromMfg)) {
        newData.gsmsCostFromMfg = '';
      } else {
        // Always show the value, even if it's 0
        // CRITICAL: Overwrite any previous manual value - goal seek takes precedence
        newData.gsmsCostFromMfg = targetCostFromMfg.toFixed(2);
       }
      
      // Use the actual discount that was used in the calculation (no hardcoded values)
      const discountPercent = (actualDiscountUsed * 100).toFixed(1);
      recalculatedFields.push(`gsmsCostFromMfg (Goal Seek: ${goalSeekReason}, Formula: Cost From Mfg = Target Net Cost / (1 - ${discountPercent}%))`);
      
      // Now let normal recalculation flow update Net Cost From Mfg from Cost From Mfg
      // This will happen in the normal calculation below
    } else {
      // Step 2: For Indirect, calculate what Net Cost From Mfg should be
      // GSMS Cost = IFF Fee + Net Cost From Mfg
      // So: Net Cost From Mfg = Target GSMS Cost - IFF Fee
      // Allow negative values (don't use Math.max) - show 0 if calculation results in 0
      const targetNetCostFromMfg = targetGsmsCost - iffFee;
      
      // Step 3: For Indirect, Net Cost From Mfg = Cost From Mfg (they're equal)
      // So: Cost From Mfg = Net Cost From Mfg
      // Always show the value, even if it's 0 or negative
      // CRITICAL: Always update gsmsCostFromMfg when goal seek runs, even if it was manually set before
      const targetCostFromMfg = targetNetCostFromMfg;
      newData.gsmsCostFromMfg = targetCostFromMfg.toFixed(2);
       recalculatedFields.push(`gsmsCostFromMfg (Goal Seek: ${goalSeekReason}, Formula: Cost From Mfg = Target GSMS Cost - IFF Fee)`);
      
      // Now let normal recalculation flow update Net Cost From Mfg from Cost From Mfg
      // This will happen in the normal calculation below
    }
  }
  
  // Re-read gsmsCostFromMfg after goal seek (if it was updated)
  // This ensures we use the updated value for Net Cost calculation
  // IMPORTANT: Only re-read if goal seek was triggered (it updates gsmsCostFromMfg)
  // Otherwise, preserve the value we set earlier to maintain consistency
  if (goalSeekTriggered) {
    gsmsCostFromMfg = parseFloat(newData.gsmsCostFromMfg) || 0;
  }
  // If goal seek wasn't triggered, keep the gsmsCostFromMfg value we parsed earlier (which may have been preserved)
  
  // IMPORTANT: After goal seek, preserve the _gsmsProfitManuallySet flag
  // This prevents the goal seek result from being overwritten when dependent fields recalculate
  if (goalSeekTriggered) {
    newData._gsmsProfitManuallySet = true;
  }
  
  // Calculate GSMS Net Cost From Mfg (dependent on GSMS Cost From Mfg)
  // This must happen AFTER goal seek updates Cost From Mfg (if goal seek was triggered)
  // For indirect products, Net Cost From Mfg = Cost From Mfg (they should be equal)
  // EXCEPTION: NC templates use productType 'indirect' for cost formulas but still apply a supplier
  // discount to Net Cost From Mfg (same as Direct). So when isNC is true, always fall through to the
  // discount path even though isIndirect is also true.
  let netCostFromMfg;
  if (isIndirect && !isNC) {
    // For non-NC indirect products, Net Cost From Mfg = Cost From Mfg (no discount)
    // Indirect products do NOT use supplier discounts
    netCostFromMfg = gsmsCostFromMfg;
  } else {
    // For Direct products OR NC templates, use supplier discount if available
    // NOTE: Only Direct and NC use supplier discounts, NOT Indirect
    // Extract supplier code from NDC (first 5 characters)
    // Use available NDC fields: selectedMfgNdc, mfg_ndc 
    const supplierBulkNdc = newData.selectedMfgNdc || newData.mfg_ndc || updatedData.selectedMfgNdc || updatedData.mfg_ndc || '';
    
    // Use supplierDiscount from newData first, then fallback to updatedData, then MobX store
    // Only use discount if it's > 0 (0 means no discount)
    let supplierDiscount = (newData.supplierDiscount !== undefined && newData.supplierDiscount !== null && newData.supplierDiscount > 0)
      ? newData.supplierDiscount
      : ((updatedData.supplierDiscount !== undefined && updatedData.supplierDiscount !== null && updatedData.supplierDiscount > 0) 
          ? updatedData.supplierDiscount 
          : undefined);
    
    // CRITICAL: If discount is still undefined, get it from MobX store
    if (supplierDiscount === undefined && supplierBulkNdc && supplierBulkNdc.length >= 5) {
      const supplierCode = supplierBulkNdc.substring(0, 5);
      const storeDiscount = bidAnalysisStore.getSupplierDiscount(supplierCode);
      if (storeDiscount !== undefined && storeDiscount !== null && storeDiscount > 0) {
        supplierDiscount = storeDiscount;
        // CRITICAL: Store the discount in newData so it's available for future recalculations
        newData.supplierDiscount = supplierDiscount;
        newData.supplierCode = supplierCode;
      }
    }
    
    // Always recalculate gsmsNetCostFromMfg with discount
    const calculatedNetCost = F.calculateGSMSNetCostFromMfg(gsmsCostFromMfg, supplierBulkNdc, supplierDiscount);
    netCostFromMfg = calculatedNetCost;
    
    // CRITICAL: Always store discount and supplier code in newData so they're available for future recalculations
    if (supplierDiscount !== undefined && supplierDiscount !== null && supplierDiscount > 0) {
      newData.supplierDiscount = supplierDiscount;
      if (supplierBulkNdc && supplierBulkNdc.length >= 5) {
        newData.supplierCode = supplierBulkNdc.substring(0, 5);
      }
    }
  }
  newData.gsmsNetCostFromMfg = netCostFromMfg.toFixed(2);
  if (changedField === 'gsmsCostFromMfg' || goalSeekTriggered || (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsNetCostFromMfg'))) {
    let formula: string;
    if (isIndirect) {
      formula = 'Net Cost From Mfg = Cost From Mfg (equal for Indirect)';
    } else {
      // For direct products, show discount if available
      const supplierDiscount = newData.supplierDiscount;
      if (supplierDiscount !== undefined && supplierDiscount !== null) {
        const discountPercent = (supplierDiscount * 100).toFixed(1);
        formula = `Net Cost From Mfg = Cost From Mfg × (1 - ${discountPercent}%)`;
      } else {
        formula = 'Net Cost From Mfg = Cost From Mfg (no discount)';
      }
    }
    recalculatedFields.push(`gsmsNetCostFromMfg (Formula: ${formula})`);
  }
  
  // Normal calculation flow (runs for all cases, including after goal seek)
  // After goal seek updates Cost From Mfg, this flow will recalculate all dependent variables
  // Calculate GSMS Cost from current values
  // IMPORTANT: When suggestedBidPrice changes, IFF Fee is recalculated above, and GSMS Cost must use the updated IFF Fee
  // NC templates use the same direct formula as Direct products (with COGS components)
  
  // Check if gsmsCost exists from API - preserve it unless dependencies changed
  // Also check _gsmsCostFromApi flag which is set when loading NC template data from API
  // CRITICAL: Check both newData and updatedData for gsmsCost value (it might be in either)
  const gsmsCostFromNewData = newData.gsmsCost !== null && 
                               newData.gsmsCost !== undefined && 
                               newData.gsmsCost !== '' &&
                               !isNaN(parseFloat(String(newData.gsmsCost)));
  const gsmsCostFromUpdatedData = updatedData.gsmsCost !== null && 
                                   updatedData.gsmsCost !== undefined && 
                                   updatedData.gsmsCost !== '' &&
                                   !isNaN(parseFloat(String(updatedData.gsmsCost)));
  
  const hasApiGsmsCost = gsmsCostFromNewData || gsmsCostFromUpdatedData || (updatedData._gsmsCostFromApi === true);
  
  // CRITICAL: If _gsmsCostFromApi flag is explicitly set, we should preserve the API value
  // This flag is set when loading data from API response, indicating the value should be preserved
  const shouldPreserveApiGsmsCost = updatedData._gsmsCostFromApi === true;
  
   
  // CRITICAL: Ensure we have the API value available - prefer updatedData if flag is set
  if (shouldPreserveApiGsmsCost && !gsmsCostFromNewData && gsmsCostFromUpdatedData) {
    // If flag is set but value is not in newData, copy it from updatedData
    newData.gsmsCost = updatedData.gsmsCost;
  }
  
  // Dependencies for gsmsCost: gsmsCostFromMfg, gsmsNetCostFromMfg, gsmsPackSize, mfgBottleSize, COGS fields, gsmsIffFee
  // Note: gsmsCostFromMfg is included because it triggers gsmsNetCostFromMfg recalculation, which then affects gsmsCost
  const gsmsCostDependencyFields = ['gsmsCostFromMfg', 'gsmsNetCostFromMfg', 'gsmsPackSize', 'mfgBottleSize', 'gsmsHndlgType', 
                                    'gsmsBottleCost', 'gsmsCapCost', 'gsmsLabelCost', 'gsmsInsertCost', 
                                    'gsmsBoxCartonCost', 'gsmsDesiccantCost', 'outsourceRepackFee', 
                                    'blendedWhlsFee', 'gsmsFrtCost', 'pv2Percent', 'gsmsLabor', 'gsmsIffFee'];
  
  // CRITICAL: If shouldPreserveApiGsmsCost is true, we should preserve the API value
  // Only recalculate if user explicitly changed GSMS Cost itself or if the calculated value would be significantly different
  // System updates (like supplier discount fetch updating gsmsCostFromMfg) should NOT trigger recalculation
  // On initial load (changedField is undefined), if flag is set, preserve API value (don't recalculate)
  
  // Check if user explicitly changed GSMS Cost itself
  const userChangedGsmsCost = changedField === 'gsmsCost' && 
                               updatedData.gsmsCost !== undefined && 
                               updatedData.gsmsCost !== null &&
                               updatedData.gsmsCost !== '' &&
                               newData.gsmsCost !== undefined &&
                               newData.gsmsCost !== null &&
                               newData.gsmsCost !== '' &&
                               updatedData.gsmsCost !== newData.gsmsCost;
  
  // When user changes gsmsCostFromMfg or gsmsNetCostFromMfg, ALWAYS recalculate gsmsCost so gsmsProfit uses the new cost
  // CRITICAL: Also recalculate when goalSeekTriggered — goal seek updates gsmsCostFromMfg in newData,
  // so gsmsCost must be recalculated to reflect the new cost from mfg.
  const shouldRecalculateGsmsCost = goalSeekTriggered || (shouldPreserveApiGsmsCost
    ? (userChangedGsmsCost || // User explicitly changed GSMS Cost
       (changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg') || // User changed cost from mfg / net cost → recalc gsmsCost so gsmsProfit updates
       (changedField !== undefined && changedField !== null && 
        (changedField === 'pv2Percent' || // User can change PV2%
         changedField === 'blendedWhlsFee' || // User can change this
         changedField === 'gsmsPackSize' || // User can change pack size
         changedField === 'mfgBottleSize' || // User can change bottle size
         changedField === 'gsmsHndlgType' || // User can change handling type
         (shouldRecalculatePv2Percent && FIELD_DEPENDENCIES['pv2Percent']?.includes('gsmsCost')))))
    : (changedField 
      ? (gsmsCostDependencyFields.includes(changedField) || 
         FIELD_DEPENDENCIES[changedField]?.includes('gsmsCost') ||
         changedField === 'pv2Percent' ||
         changedField === 'blendedWhlsFee' ||
         (shouldRecalculatePv2Percent && FIELD_DEPENDENCIES['pv2Percent']?.includes('gsmsCost')))
      : (!hasApiGsmsCost)));
  
  if (isDirect || isNC) {
    // Direct: Complex formula with all COGS components
    // CRITICAL: Always use the current values from newData to ensure we use the updated values
    // This is especially important when mfgBottleSize or gsmsPackSize changes
    const currentMfgBottleSize = parseFloat(newData.mfgBottleSize) || mfgBottleSize;
    const currentPackSize = parseFloat(newData.gsmsPackSize) || packSize;
    
    
    
    // Re-parse bottle capacity if pack size changed (to ensure we use updated capacity)
    // For NC templates, also recalculate when mfgBottleSize changes (affects GSMS Cost calculation)
    let currentBottleCapacity = bottleCapacity;
    const shouldRecalcCogs = changedField === 'gsmsPackSize' || (isNC && changedField === 'mfgBottleSize');
     
    
    if (shouldRecalcCogs) {
      const updatedPackSize = parseFloat(newData.gsmsPackSize) || 0;
      currentBottleCapacity = isNC
        ? F.calculateNCGSMSBottleCapacity(handlingType, updatedPackSize)
        : F.calculateGSMSBottleCapacity(handlingType, updatedPackSize);
      // Recalculate bottle cost with updated capacity
      const updatedBottleCost = isNC
        ? F.calculateNCGSMSBottleCost(handlingType, currentBottleCapacity)
        : F.calculateGSMSBottleCost(handlingType, currentBottleCapacity);
      // Update bottleCost variable for GSMS Cost calculation
      const updatedBottleCostNum = updatedBottleCost;
      // Recalculate dependent costs with updated bottle capacity and cost
      const updatedCapCost = F.calculateGSMSCapCost(updatedBottleCostNum, currentBottleCapacity);
      const updatedLabelCost = F.calculateGSMSLabelCost(handlingType, updatedBottleCostNum);
      const updatedInsertCost = isNC && supplierName
        ? F.calculateNCGSMSInsertCost(updatedBottleCostNum, supplierName)
        : F.calculateGSMSInsertCost(updatedBottleCostNum, description);
      const updatedBoxCartonCost = F.calculateGSMSBoxCartonCost(updatedBottleCostNum, currentBottleCapacity);
      const updatedFrtCost = isNC
        ? F.calculateNCGSMSFrtCost(currentBottleCapacity, outsourceRepackFee)
        : F.calculateGSMSFrtCost(currentBottleCapacity, outsourceRepackFee);
      const updatedPv2Percent = F.calculatePV2Percent(currentBottleCapacity, wac);
      const updatedLabour = isNC
        ? F.calculateNCGSMSLabor(currentBottleCapacity, updatedBottleCostNum, outsourceRepackFee)
        : F.calculateGSMSLabor(handlingType, suggestedBidPrice);
      
      // Use updated values for GSMS Cost calculation
      // When COGS are recalculated (pack size or mfgBottleSize changed), always recalculate gsmsCost
      const gsmsNetCostFromMfgValue = parseFloat(newData.gsmsNetCostFromMfg) || 0;
      gsmsCost = F.calculateGSMSCost({
        gsmsNetCostFromMfg: gsmsNetCostFromMfgValue,
        mfgBottleSize: currentMfgBottleSize,
        gsmsPackSize: currentPackSize,
        gsmsBottleCost: updatedBottleCostNum,
        gsmsCapCost: updatedCapCost,
        gsmsLabelCost: updatedLabelCost,
        gsmsInsertCost: updatedInsertCost,
        gsmsIffFee: iffFee,
        gsmsBoxCartonCost: updatedBoxCartonCost,
        gsmsDesiccantCost: parseFloat(newData.gsmsDesiccantCost) || 0,
        outsourceRepackFee: outsourceRepackFee,
        blendedWhlsFee: parseFloat(newData.blendedWhlsFee) || 0,
        gsmsFrtCost: updatedFrtCost,
        pv2Percent: updatedPv2Percent,
        gsmsLabor: updatedLabour,
      });
      const previousGsmsCost = newData.gsmsCost || updatedData.gsmsCost || 'N/A';
      newData.gsmsCost = safeToFixed(gsmsCost, 2);
      if (previousGsmsCost !== newData.gsmsCost && previousGsmsCost !== 'N/A') {
        const _yearLabel = isNC 
          ? (yearIndex === undefined || yearIndex === null || yearIndex === 0 
              ? 'Base Year (0)' 
              : `Option Year ${yearIndex}`)
          : (isDirect ? 'MAS Direct' : 'MAS Indirect');
      }
       
       
    } else {
      // Normal calculation path - always use current values from newData
      // IMPORTANT: For NC templates, mfgBottleSize is used directly in the formula, so always use currentMfgBottleSize
      // Only recalculate gsmsCost if dependencies changed OR if no API value exists
      // Also recalculate if blendedWhlsFee changed (it's a dependency of gsmsCost)
      // Check if blendedWhlsFee was marked as changed during this calculation
      const blendedWhlsFeeChanged = (newData as any)._blendedWhlsFeeChanged === true ||
        (newData.blendedWhlsFee !== undefined && 
         updatedData.blendedWhlsFee !== undefined &&
         newData.blendedWhlsFee !== updatedData.blendedWhlsFee);
      
      // CRITICAL: When blendedWhlsFeeChanged, ALWAYS recalculate gsmsCost - it's a direct input to the formula
      // Don't preserve API value when blendedWhlsFee changed (e.g. from suggestedBidPrice, targetLaunchDate, vaAnnualNcEst)
      if (shouldPreserveApiGsmsCost) {
        // Preserve API value - don't recalculate unless user explicitly changed a dependency OR blendedWhlsFee changed
        if (!shouldRecalculateGsmsCost && !blendedWhlsFeeChanged) {
          // Preserve the API value - ensure it's available
          const apiCostValue = parseFloat(newData.gsmsCost) || parseFloat(updatedData.gsmsCost) || 0;
          gsmsCost = apiCostValue;
          // Ensure the value is set in newData if it's not already there
          if (!newData.gsmsCost || newData.gsmsCost === '' || newData.gsmsCost === '0.00') {
            if (updatedData.gsmsCost && updatedData.gsmsCost !== '' && updatedData.gsmsCost !== '0.00') {
              newData.gsmsCost = safeToFixed(parseFloat(updatedData.gsmsCost), 2);
            }
          }
        } else {
          // User changed a dependency field - but check if it's a system update first
          // If gsmsCostFromMfg or gsmsNetCostFromMfg changed but API value exists, preserve API value
          const isSystemUpdate = (changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg') && 
                                 updatedData._gsmsCostFromApi === true &&
                                 updatedData.gsmsCost && 
                                 updatedData.gsmsCost !== '' && 
                                 updatedData.gsmsCost !== '0.00';
          
          if (isSystemUpdate) {
            // System update (like supplier discount) - preserve API value
            const apiCostValue = parseFloat(updatedData.gsmsCost) || parseFloat(newData.gsmsCost) || 0;
            gsmsCost = apiCostValue;
            if (!newData.gsmsCost || newData.gsmsCost === '' || newData.gsmsCost === '0.00') {
              if (updatedData.gsmsCost && updatedData.gsmsCost !== '' && updatedData.gsmsCost !== '0.00') {
                newData.gsmsCost = safeToFixed(parseFloat(updatedData.gsmsCost), 2);
              }
            } else {
              // Ensure API value is preserved
              newData.gsmsCost = safeToFixed(apiCostValue, 2);
            }
            // Keep the flag
            newData._gsmsCostFromApi = true;
          } else {
            // User changed a dependency field - recalculate
            const gsmsNetCostFromMfgValue = parseFloat(newData.gsmsNetCostFromMfg) || 0;
            gsmsCost = F.calculateGSMSCost({
              gsmsNetCostFromMfg: gsmsNetCostFromMfgValue,
              mfgBottleSize: currentMfgBottleSize,
              gsmsPackSize: currentPackSize,
              gsmsBottleCost: bottleCost,
              gsmsCapCost: capCost,
              gsmsLabelCost: labelCost,
              gsmsInsertCost: insertCost,
              gsmsIffFee: iffFee,
              gsmsBoxCartonCost: boxCartonCost,
              gsmsDesiccantCost: parseFloat(newData.gsmsDesiccantCost) || 0,
              outsourceRepackFee: outsourceRepackFee,
              blendedWhlsFee: parseFloat(newData.blendedWhlsFee) || 0,
              gsmsFrtCost: frtCost,
              pv2Percent: pv2Percent,
              gsmsLabor: labour,
            });
            newData.gsmsCost = safeToFixed(gsmsCost, 2);
            // Clear the flag since we recalculated
            newData._gsmsCostFromApi = false;
          }
        }
      } else if (shouldRecalculateGsmsCost || !hasApiGsmsCost || blendedWhlsFeeChanged) {
        if (blendedWhlsFeeChanged && !shouldRecalculateGsmsCost) {
          const _yearLabel = isNC 
            ? (yearIndex === undefined || yearIndex === null || yearIndex === 0 
                ? 'Base Year (0)' 
                : `Option Year ${yearIndex}`)
            : (isDirect ? 'MAS Direct' : 'MAS Indirect');
        }
        const gsmsNetCostFromMfgValue = parseFloat(newData.gsmsNetCostFromMfg) || 0;
        gsmsCost = F.calculateGSMSCost({
          gsmsNetCostFromMfg: gsmsNetCostFromMfgValue,
          mfgBottleSize: currentMfgBottleSize, // Always use current value - this ensures mfgBottleSize changes are reflected
          gsmsPackSize: currentPackSize, // Always use current value
          gsmsBottleCost: bottleCost,
          gsmsCapCost: capCost,
          gsmsLabelCost: labelCost,
          gsmsInsertCost: insertCost,
          gsmsIffFee: iffFee, // This uses the updated IFF Fee calculated from suggestedBidPrice
          gsmsBoxCartonCost: boxCartonCost,
          gsmsDesiccantCost: parseFloat(newData.gsmsDesiccantCost) || 0,
          outsourceRepackFee: outsourceRepackFee,
          blendedWhlsFee: parseFloat(newData.blendedWhlsFee) || 0,
          gsmsFrtCost: frtCost,
          pv2Percent: pv2Percent,
          gsmsLabor: labour,
        });
        const previousGsmsCost = newData.gsmsCost || updatedData.gsmsCost || 'N/A';
        newData.gsmsCost = safeToFixed(gsmsCost, 2);
        if (previousGsmsCost !== newData.gsmsCost && previousGsmsCost !== 'N/A') {
          const _yearLabel = isNC 
            ? (yearIndex === undefined || yearIndex === null || yearIndex === 0 
                ? 'Base Year (0)' 
                : `Option Year ${yearIndex}`)
            : (isDirect ? 'MAS Direct' : 'MAS Indirect');
        }
       } else {
        // Preserve API value for gsmsCost
        gsmsCost = parseFloat(newData.gsmsCost) || 0;
      
      }
    }
    
    // Calculate GSMS Marketing Fee for Direct products
    // Formula: IF GSMS Bottle Capacity == " " THEN return Suggested Bid Price - GSMS Net Cost From Mfg, ELSE return 0
    // Always recalculate for direct products to ensure it's up to date
    // Marketing fee should recalculate when: suggestedBidPrice, gsmsNetCostFromMfg, bottleCapacity, or handling type changes
    if (suggestedBidPrice > 0) {
      const gsmsNetCostFromMfg = parseFloat(newData.gsmsNetCostFromMfg) || 0;
      const gsmsMarketingFee = F.calculateGSMSMarketingFee(
        suggestedBidPrice, 
        gsmsNetCostFromMfg, 
        bottleCapacity, 
        true // isDirect = true
      );
      newData.gsmsMarketingFee = gsmsMarketingFee.toFixed(2);
      
      // Log if triggered by dependent field changes
      const shouldLogMarketingFee = 
        !changedField || 
        changedField === 'suggestedBidPrice' || 
        changedField === 'gsmsNetCostFromMfg' || 
        changedField === 'gsmsBottleCapacity' || 
        changedField === 'gsmsHndlgType' ||
        changedField === 'gsmsPackSize' || // Pack size affects bottle capacity which affects marketing fee
        (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsMarketingFee'));
      
      if (shouldLogMarketingFee && (bottleCapacity === '' || bottleCapacity === ' ')) {
        recalculatedFields.push(`gsmsMarketingFee (Formula: GSMS Marketing Fee = Suggested Bid Price - GSMS Net Cost From Mfg)`);
      }
    } else {
      // Set to 0 if suggested bid price is 0
      newData.gsmsMarketingFee = '0.00';
    }
  } else {
    // Indirect: Simple formula (IFF Fee + Net Cost From Mfg)
    // Only for MAS Indirect products - NC templates use direct formula (handled above)
    // For Indirect, also check if we should preserve API value
    // Note: Indirect doesn't use blendedWhlsFee in the formula, but check for consistency
    const _blendedWhlsFeeChanged = (newData as any)._blendedWhlsFeeChanged === true ||
      (newData.blendedWhlsFee !== undefined && 
       updatedData.blendedWhlsFee !== undefined &&
       newData.blendedWhlsFee !== updatedData.blendedWhlsFee);
    
    // CRITICAL: If shouldPreserveApiGsmsCost is true, preserve API value unless user explicitly changed a dependency
    if (shouldPreserveApiGsmsCost && !shouldRecalculateGsmsCost) {
      // Preserve API value - don't recalculate
      gsmsCost = parseFloat(newData.gsmsCost) || 0;
    } else if (shouldRecalculateGsmsCost || !hasApiGsmsCost) {
      gsmsCost = F.calculateGSMSCostIndirect(
        iffFee, // This uses the updated IFF Fee calculated from suggestedBidPrice
        parseFloat(newData.gsmsNetCostFromMfg) || 0
      );
     } else {
      // Preserve API value for Indirect
      gsmsCost = parseFloat(newData.gsmsCost) || 0;
     }
    
    // Calculate GSMS Marketing Fee for Indirect products
    // Formula: GSMS Marketing Fee = Suggested Bid Price - GSMS Net Cost From Mfg
    if (suggestedBidPrice > 0) {
      const gsmsNetCostFromMfg = parseFloat(newData.gsmsNetCostFromMfg) || 0;
      const gsmsMarketingFee = F.calculateGSMSMarketingFee(suggestedBidPrice, gsmsNetCostFromMfg);
      newData.gsmsMarketingFee = gsmsMarketingFee.toFixed(2);
      
      // Log if triggered by dependent field changes
      if (changedField === 'suggestedBidPrice' || changedField === 'gsmsNetCostFromMfg' || 
          (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsMarketingFee'))) {
        recalculatedFields.push(`gsmsMarketingFee (Formula: GSMS Marketing Fee = Suggested Bid Price - GSMS Net Cost From Mfg)`);
      }
    } else {
      newData.gsmsMarketingFee = '0.00';
    }
  }
  
  // Set gsmsCost in newData - it was either calculated above or preserved from API
  // If gsmsCost was preserved from API and not recalculated, it's already in newData, so don't overwrite
  // Otherwise, set it from the calculated value
  // CRITICAL: If shouldPreserveApiGsmsCost is true and we didn't recalculate, preserve the original API value
  if (shouldPreserveApiGsmsCost && !shouldRecalculateGsmsCost) {
    // Preserve the exact API value - don't recalculate or reformat
    // The value should already be in newData from the API response, but ensure it's there
    const apiGsmsCostValue = parseFloat(newData.gsmsCost) || parseFloat(updatedData.gsmsCost) || 0;
    if (!isNaN(apiGsmsCostValue) && apiGsmsCostValue > 0) {
      // Use the API value - prefer newData, fallback to updatedData
      if (newData.gsmsCost && newData.gsmsCost !== '' && newData.gsmsCost !== '0.00') {
        // Already in newData, just ensure formatting
        newData.gsmsCost = safeToFixed(parseFloat(newData.gsmsCost), 2);
      } else if (updatedData.gsmsCost && updatedData.gsmsCost !== '' && updatedData.gsmsCost !== '0.00') {
        // Get from updatedData
        newData.gsmsCost = safeToFixed(parseFloat(updatedData.gsmsCost), 2);
      }
      // Preserve the flag
      newData._gsmsCostFromApi = true;
    }
  } else if (shouldRecalculateGsmsCost || !hasApiGsmsCost) {
    // Value was recalculated - set it
    newData.gsmsCost = safeToFixed(gsmsCost, 2);
    // Clear the flag since we recalculated
    if (shouldRecalculateGsmsCost) {
      newData._gsmsCostFromApi = false;
    }
  } else {
    // Value was preserved from API - it's already in newData, but ensure it's formatted correctly
    // Parse and reformat to ensure consistency
    const apiGsmsCostValue = parseFloat(newData.gsmsCost) || parseFloat(updatedData.gsmsCost) || 0;
    if (!isNaN(apiGsmsCostValue) && apiGsmsCostValue > 0) {
      if (newData.gsmsCost && newData.gsmsCost !== '' && newData.gsmsCost !== '0.00') {
        newData.gsmsCost = safeToFixed(parseFloat(newData.gsmsCost), 2);
      } else if (updatedData.gsmsCost && updatedData.gsmsCost !== '' && updatedData.gsmsCost !== '0.00') {
        newData.gsmsCost = safeToFixed(parseFloat(updatedData.gsmsCost), 2);
      }
    }
  }
  
   
  
  // Log GSMS Cost recalculation if it was triggered by a dependent field change or goal seek
  if (goalSeekTriggered || (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsCost'))) {
    const formula = isDirect || isNC
      ? 'GSMS Cost = Net Cost From Mfg + IFF Fee + all COGS components'
      : 'GSMS Cost = IFF Fee + Net Cost From Mfg';
    const reason = goalSeekTriggered ? 'Goal Seek' : 'Dependent field change';
    recalculatedFields.push(`gsmsCost (${reason}, Formula: ${formula})`);
  }
  
  // Ensure gsmsCost is valid for GSMS Profit calculation
  const costForProfitCalc = isNaN(gsmsCost) ? 0 : gsmsCost;
  
  // IMPORTANT: Preserve suggestedBidPrice in newData if it exists in updatedData
  // This ensures the value is not lost during recalculation
  if (updatedData.suggestedBidPrice !== undefined && updatedData.suggestedBidPrice !== null) {
    // Preserve the original value (could be number or string)
    newData.suggestedBidPrice = updatedData.suggestedBidPrice;
  }
  
  // When suggestedBidPrice changes: only clear gsmsProfit if it was NOT manually set.
  // If the user had manually set profit (or copied it), we must not delete it so preservation logic can keep it.
  const suggestedBidPriceChanging = changedField === 'suggestedBidPrice';
  const profitWasManuallySet = updatedData._gsmsProfitManuallySet === true ||
    updatedData.gsmsProfitManuallySet === true ||
    (updatedData.gsmsProfit != null && updatedData.gsmsProfit !== '' &&
      parseFloat(String(updatedData.gsmsProfit)) !== 0);
  if (suggestedBidPriceChanging && !profitWasManuallySet) {
    delete newData.gsmsProfit;
    newData._gsmsProfitManuallySet = false;
  }

  // Calculate Total TMS% (skip for NC template)
  let targetUsage = 0;
  let revenue = 0;
  
  if (!isNC) {
    const totalTms = F.calculateTotalTmsPercent(vaUsage, vaTms, dodUsage, dodTms);
    newData.totalTmsPercent = safeToFixed(totalTms, 2);

    // Calculate Target Annual Usage (skip for NC template)
    targetUsage = F.calculateTargetAnnualUsage(vaUsage, dodUsage, totalTms);
    newData.targetAnnualUsage = safeToFixed(targetUsage, 2);

    // Calculate Contract Annual Revenue
    revenue = F.calculateContractAnnualRevenue(targetUsage, suggestedBidPrice);
    newData.contractAnnualRevenue = safeToFixed(revenue, 2);
  } else {
    // For NC template, skip TMS% calculations
    // Calculate NC EST Annual Usage = VA Annual NC EST + DOD Annual NC EST + IHS Annual NC EST + BOP Annual NC EST
    const ncEstAnnualUsage = vaAnnualNcEst + dodAnnualNcEst + ihsAnnualNcEst + bopAnnualNcEst;
    newData.ncEstAnnualUsage = safeToFixed(ncEstAnnualUsage, 2);
    
    // Calculate Contract Annual Revenue using nc_est_annual_usage
    revenue = F.calculateContractAnnualRevenue(ncEstAnnualUsage, suggestedBidPrice);
    newData.contractAnnualRevenue = safeToFixed(revenue, 2);
    targetUsage = ncEstAnnualUsage; // Use nc_est_annual_usage for NC template
  }

  // Calculate GSMS Profit
  // Formula: (Suggested Bid Price - GSMS Cost) / Suggested Bid Price * 100
  // Always recalculate when Suggested Bid Price or GSMS Cost changes (unless user just manually changed GSMS Profit)
  let gsmsProfit;
  
  // Check if GSMS Profit was manually changed (user input)
  const isGsmsProfitManuallyChanged = changedField === 'gsmsProfit';

  // CRITICAL: When targetLaunchDate changes (e.g. tab switch after Copy for all years), we recalculate
  // blendedWhlsFee which changes gsmsCost. We must NOT overwrite gsmsProfit in this case - the user
  // explicitly copied the same value (e.g. 14%) to all years. Preserve it.
  const isTargetLaunchDateOnlyChange = changedField === 'targetLaunchDate';
  const shouldPreserveGsmsProfitForDateChange =
    isTargetLaunchDateOnlyChange &&
    (updatedData._gsmsProfitManuallySet === true || updatedData.gsmsProfitManuallySet === true) &&
    updatedData.gsmsProfit !== undefined &&
    updatedData.gsmsProfit !== null &&
    updatedData.gsmsProfit !== '' &&
    parseFloat(String(updatedData.gsmsProfit)) !== 0;

  // Check if GSMS Profit was manually set in the input data (from previous manual changes)
  // Also check if gsmsProfit has a non-zero value, which indicates it was manually set
  const wasManuallySet = updatedData._gsmsProfitManuallySet === true ||
                         updatedData.gsmsProfitManuallySet === true ||
                         (updatedData.gsmsProfit !== null &&
                          updatedData.gsmsProfit !== undefined &&
                          updatedData.gsmsProfit !== '' &&
                          updatedData.gsmsProfit !== 0 &&
                          updatedData.gsmsProfit !== '0' &&
                          updatedData.gsmsProfit !== '0.00' &&
                          parseFloat(updatedData.gsmsProfit) !== 0);
  
  

  
  // Track if we should preserve the manual value
  let shouldPreserveManualGsmsProfit = false;
  
  // CRITICAL: When gsmsCostFromMfg or gsmsNetCostFromMfg changes, ALWAYS recalculate GSMS profit% (two-way binding).
  // Clear manual flag so profit is recalculated from formula regardless of previous manual setting.
  const costFromMfgOrNetCostChanged = changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg';
  if (costFromMfgOrNetCostChanged) {
    newData._gsmsProfitManuallySet = false;
  }
  
  // CRITICAL: When suggestedBidPrice changes, check if gsmsProfit was manually set
  // If it was manually set, preserve it; otherwise recalculate
  const isSuggestedBidPriceChanging = changedField === 'suggestedBidPrice';
  
  // CRITICAL: Special handling for when suggestedBidPrice changes but gsmsProfit was manually set
  // Preserve the exact value (e.g. 15 stays 15) so formula rounding from GSMS Cost does not change it.
  if (isSuggestedBidPriceChanging && wasManuallySet && !isGsmsProfitManuallyChanged && !goalSeekTriggered) {
    if (updatedData.gsmsProfit !== undefined && updatedData.gsmsProfit !== null && updatedData.gsmsProfit !== '') {
      const preservedValue = parseFloat(updatedData.gsmsProfit);
      if (!isNaN(preservedValue)) {
        gsmsProfit = preservedValue;
        if (typeof updatedData.gsmsProfit === 'number') {
          newData.gsmsProfit = updatedData.gsmsProfit;
        } else if (typeof updatedData.gsmsProfit === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(String(updatedData.gsmsProfit))) {
          newData.gsmsProfit = String(updatedData.gsmsProfit).trim();
        } else {
          newData.gsmsProfit = preservedValue;
        }
        newData._gsmsProfitManuallySet = true;
        shouldPreserveManualGsmsProfit = true;
      }
    }
  }
  
  // EARLY: When targetLaunchDate-only change with manually set profit (e.g. after Copy for all years),
  // preserve the exact gsmsProfit so blendedWhlsFee→gsmsCost recalc does not overwrite it with formula (e.g. 15.02).
  if (shouldPreserveGsmsProfitForDateChange) {
    const preservedValue = parseFloat(String(updatedData.gsmsProfit));
    if (!isNaN(preservedValue)) {
      gsmsProfit = preservedValue;
      if (typeof updatedData.gsmsProfit === 'number') {
        newData.gsmsProfit = updatedData.gsmsProfit;
      } else if (typeof updatedData.gsmsProfit === 'string') {
        newData.gsmsProfit = String(updatedData.gsmsProfit).trim();
      } else {
        newData.gsmsProfit = preservedValue;
      }
      newData._gsmsProfitManuallySet = true;
      shouldPreserveManualGsmsProfit = true;
    }
  }

  // If GSMS Profit was NOT manually changed in this recalculation, check if it was previously manually set
  // If it was manually set, preserve it unless the user explicitly changed Cost From Mfg / Net Cost (two-way binding).
  // CRITICAL: When profit was manually set (or copied via "Copy for all years"), preserve the EXACT value so we
  // never overwrite with formula result (e.g. 15% must stay 15%, not become 15.02% due to rounding in GSMS Cost).
  if (!shouldPreserveManualGsmsProfit && !isGsmsProfitManuallyChanged && wasManuallySet && !goalSeekTriggered && !isSuggestedBidPriceChanging) {
    // Only recalculate when user explicitly changed gsmsCostFromMfg or gsmsNetCostFromMfg (two-way binding).
    // For any other trigger (blendedWhlsFee, targetLaunchDate, gsmsCost drift from rounding, etc.) preserve manual value.
    const costFieldsChanged = changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg';
    const shouldPreserveManualValue = !costFieldsChanged;
    
    
    if (shouldPreserveManualValue && updatedData.gsmsProfit !== undefined && updatedData.gsmsProfit !== null && updatedData.gsmsProfit !== '') {
      const preservedValue = parseFloat(updatedData.gsmsProfit);
      if (!isNaN(preservedValue)) {
        gsmsProfit = preservedValue;
        // CRITICAL: Preserve the exact value the user entered (e.g. 15 stays 15, not 15.00 then formula 15.02).
        // Use the raw value so rounding in GSMS Cost From Mfg does not change the displayed profit %.
        if (typeof updatedData.gsmsProfit === 'number') {
          newData.gsmsProfit = updatedData.gsmsProfit;
        } else if (typeof updatedData.gsmsProfit === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(String(updatedData.gsmsProfit))) {
          newData.gsmsProfit = String(updatedData.gsmsProfit).trim();
        } else {
          newData.gsmsProfit = preservedValue;
        }
        newData._gsmsProfitManuallySet = true;
        shouldPreserveManualGsmsProfit = true;
      }
    } 
  }
  
  // CRITICAL: Detect if gsmsCost changed by comparing old and new values
  // This ensures gsmsProfit is recalculated whenever gsmsCost changes, regardless of how it changed
  const oldGsmsCostValue = updatedData.gsmsCost !== undefined && updatedData.gsmsCost !== null && updatedData.gsmsCost !== ''
    ? parseFloat(String(updatedData.gsmsCost))
    : null;
  const newGsmsCostValue = newData.gsmsCost !== undefined && newData.gsmsCost !== null && newData.gsmsCost !== ''
    ? parseFloat(String(newData.gsmsCost))
    : null;
  const gsmsCostValueChanged = oldGsmsCostValue !== null && newGsmsCostValue !== null && 
                               !isNaN(oldGsmsCostValue) && !isNaN(newGsmsCostValue) &&
                               Math.abs(oldGsmsCostValue - newGsmsCostValue) > 0.001;
  
  // If GSMS Profit was NOT manually changed AND we're not preserving a manual value, always recalculate based on formula
  // This ensures GSMS Profit updates whenever suggested bid price or cost changes
  // IMPORTANT: Don't recalculate if goal seek was just triggered (goal seek preserves the manual value)
  // IMPORTANT: When suggestedBidPrice changes, preserve manually set gsmsProfit instead of recalculating
  // CRITICAL: Only recalculate when suggestedBidPrice changes if gsmsProfit was NOT manually set
  // CRITICAL: Also check if shouldPreserveManualGsmsProfit was set (from the special handling above)
  // CRITICAL: Skip recalculation entirely if we've already preserved the manual value
  // CRITICAL: Also recalculate when fields that affect gsmsCost change (gsmsCostFromMfg, gsmsNetCostFromMfg, etc.)
  // These fields trigger gsmsCost recalculation, which should then trigger gsmsProfit recalculation
  // CRITICAL: Also recalculate when gsmsCost value itself changed (detected by comparing old vs new)
  const costAffectingFieldsChanged = (changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg' || 
                                       changedField === 'gsmsPackSize' || changedField === 'mfgBottleSize' ||
                                       changedField === 'gsmsHndlgType' || changedField === 'pv2Percent' ||
                                       changedField === 'blendedWhlsFee' || changedField === 'gsmsIffFee' ||
                                       changedField === 'gsmsCost' || // Also recalculate when gsmsCost itself changes
                                       (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsCost')));
  // CRITICAL: When gsmsCostFromMfg or gsmsNetCostFromMfg changes, ALWAYS recalculate gsmsProfit (two-way binding)
  // Even if gsmsProfit was manually set, it should be recalculated when cost from mfg changes
  const costFromMfgChanged = changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg';
  const shouldRecalcDueToCostChange = (costAffectingFieldsChanged || updatedData._forceRecalculateGsmsProfit === true || gsmsCostValueChanged) && 
                                      (!wasManuallySet || costFromMfgChanged) && // Allow recalculation if cost from mfg changed (two-way binding)
                                      !shouldPreserveManualGsmsProfit;

  
  if (!shouldPreserveManualGsmsProfit && ((isSuggestedBidPriceChanging && !wasManuallySet) || (!isGsmsProfitManuallyChanged && !goalSeekTriggered && !isSuggestedBidPriceChanging) || shouldRecalcDueToCostChange)) {
    // Recalculate GSMS Profit using the formula when Suggested Bid Price or GSMS Cost changes
    // This includes when suggestedBidPrice changes, which updates IFF Fee → GSMS Cost → GSMS Profit
    // Allow negative profit values (cost > price scenario)
     
    if (suggestedBidPrice > 0) {
      // Always calculate profit when suggestedBidPrice > 0, even if cost is 0 or invalid
      // Use costForProfitCalc (defaults to 0 if NaN)
      const costToUse = isNaN(costForProfitCalc) ? 0 : Math.max(0, costForProfitCalc);
      gsmsProfit = F.calculateGSMSProfit(suggestedBidPrice, costToUse);
      newData.gsmsProfit = gsmsProfit.toFixed(2); // Can be negative

      
      // Always log when GSMS Profit is recalculated (triggered by suggestedBidPrice, gsmsCost, gsmsCostFromMfg, or gsmsNetCostFromMfg changes)
      // CRITICAL: Also log when gsmsCost value changed (detected by comparing old vs new value)
      if (changedField === 'suggestedBidPrice' || changedField === 'gsmsCost' || 
          changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg' || 
          shouldRecalcDueToCostChange || gsmsCostValueChanged ||
          (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsProfit'))) {
        recalculatedFields.push(`gsmsProfit (Formula: GSMS Profit = (Suggested Bid Price - GSMS Cost) / Suggested Bid Price × 100)`);
      }
      // Clear manual flag when recalculated from formula
      newData._gsmsProfitManuallySet = false;
      
     } else {
      // Suggested bid price is 0 or invalid - but only set to 0 if gsmsProfit was NOT manually set
      if (!wasManuallySet && !shouldPreserveManualGsmsProfit) {
        gsmsProfit = 0;
        newData.gsmsProfit = '0.00';
        newData._gsmsProfitManuallySet = false;
        // Log when GSMS Profit is set to 0 due to suggestedBidPrice being 0
        if (changedField === 'suggestedBidPrice') {
          recalculatedFields.push(`gsmsProfit (Set to 0.00% - Suggested Bid Price is 0)`);
        }
      }
      // If wasManuallySet is true, preserve the existing value (already handled above)
    }
  } else if (isGsmsProfitManuallyChanged) {
    // User manually changed GSMS Profit - use the manual value and trigger goal seek (already done above)
    // IMPORTANT: Preserve the manually set value even if goal seek was triggered
    // Goal seek updates Cost From Mfg, but we need to preserve the GSMS Profit value
    if (!isNaN(gsmsProfitInput) && gsmsProfitInput >= -100 && gsmsProfitInput <= 100) {
      // Set the manually changed value
      gsmsProfit = gsmsProfitInput;
      newData.gsmsProfit = gsmsProfit.toFixed(2);
      newData._gsmsProfitManuallySet = true;
      
      // Goal seek should have already been triggered above if suggestedBidPrice > 0
      if (suggestedBidPrice > 0) {
        recalculatedFields.push(`gsmsProfit (Manually set to ${gsmsProfit}% - goal seek triggered)`);
      } else {
        recalculatedFields.push(`gsmsProfit (Manually set to ${gsmsProfit}% - goal seek pending suggested bid price)`);
      }
      
      // The goal seek logic above already handles updating independent variables
      // After goal seek updates Cost From Mfg, the normal flow recalculates dependent variables
      // So we don't need to do anything extra here - the goal seek already handled it
    } else {
      // Invalid input, recalculate - allow negative profit values
      if (suggestedBidPrice > 0 && !isNaN(costForProfitCalc) && costForProfitCalc >= 0) {
        gsmsProfit = F.calculateGSMSProfit(suggestedBidPrice, costForProfitCalc);
        newData.gsmsProfit = gsmsProfit.toFixed(2); // Can be negative
        newData._gsmsProfitManuallySet = false;
        if (changedField && FIELD_DEPENDENCIES[changedField]?.includes('gsmsProfit')) {
          recalculatedFields.push(`gsmsProfit (Formula: GSMS Profit = (Suggested Bid Price - GSMS Cost) / Suggested Bid Price × 100)`);
        }
      } else {
        gsmsProfit = 0;
        newData.gsmsProfit = '0.00';
        newData._gsmsProfitManuallySet = false;
      }
    }
  }
  
  // Ensure GSMS Profit is always set (never null, undefined, or empty string)
  // Allow negative values - only default to 0 if truly empty/invalid
  // IMPORTANT: Don't overwrite manually set values
  // CRITICAL: If gsmsProfit was manually set and we're recalculating due to suggestedBidPrice change, preserve it
  if (shouldPreserveManualGsmsProfit && newData.gsmsProfit !== undefined && newData.gsmsProfit !== null && newData.gsmsProfit !== '') {
    // Already preserved above, don't overwrite
  } else if ((newData.gsmsProfit === null || newData.gsmsProfit === undefined || newData.gsmsProfit === '' || newData.gsmsProfit === 'null' || newData.gsmsProfit === 'undefined') && 
      !newData._gsmsProfitManuallySet) {
    newData.gsmsProfit = '0.00';
    gsmsProfit = 0;
    newData._gsmsProfitManuallySet = false;
  } else if (isGsmsProfitManuallyChanged && !isNaN(gsmsProfitInput)) {
    // Ensure manually set value is preserved
    newData.gsmsProfit = gsmsProfitInput.toFixed(2);
    newData._gsmsProfitManuallySet = true;
  }


  // Calculate Contract Annual GP
  // IMPORTANT: Only recalculate Contract Annual GP when contractAnnualRevenue changes, NOT when gsmsProfit changes
  // This prevents Gross Profit from changing when GSMS Profit changes
  // Gross Profit should be independent and calculated from Contract Annual GP and Contract Annual Revenue
  // CRITICAL: Don't recalculate Contract Annual GP when cost fields change (gsmsCostFromMfg, gsmsNetCostFromMfg, gsmsCost)
  // Cost fields affect gsmsProfit, but Contract Annual GP should be preserved to maintain Gross Profit
  const isCostFieldChange = changedField === 'gsmsCostFromMfg' || changedField === 'gsmsNetCostFromMfg' || changedField === 'gsmsCost';
  
  const shouldRecalculateContractAnnualGP = !isCostFieldChange && (
                                           changedField === 'contractAnnualRevenue' || 
                                           changedField === 'targetAnnualUsage' ||
                                           changedField === 'ncEstAnnualUsage' ||
                                           changedField === 'vaAnnualUsage' ||
                                           changedField === 'dodAnnualUsage' ||
                                           changedField === 'vaAnnualNcEst' ||
                                           changedField === 'dodAnnualNcEst' ||
                                           changedField === 'ihsAnnualNcEst' ||
                                           changedField === 'bopAnnualNcEst' ||
                                           (changedField && FIELD_DEPENDENCIES[changedField]?.includes('contractAnnualRevenue')) ||
                                           !changedField); // Recalculate on initial load
  
  if (shouldRecalculateContractAnnualGP && changedField !== 'gsmsProfit') {
    // Normal recalculation for revenue-related fields
    const gsmsProfitForCalc = gsmsProfit !== undefined && !isNaN(gsmsProfit) ? gsmsProfit : 0;
    // Prefer freshly computed revenue (already recalculated above with current suggestedBidPrice)
    // Fall back to updatedData.contractAnnualRevenue only if the computed revenue is unavailable
    const validRevenue = (!isNaN(revenue) && revenue !== 0)
      ? revenue
      : (updatedData.contractAnnualRevenue !== undefined && updatedData.contractAnnualRevenue !== null && updatedData.contractAnnualRevenue !== '' 
          ? parseFloat(updatedData.contractAnnualRevenue) 
          : 0);
    const contractAnnualGPValue = F.calculateContractAnnualGP(validRevenue, gsmsProfitForCalc);
    newData.contractAnnualGP = safeToFixed(contractAnnualGPValue, 2);
  } else if (isCostFieldChange) {
    // CRITICAL: When cost fields change, preserve Contract Annual GP if it exists and is valid
    // If it doesn't exist or is 0, recalculate it using current contractAnnualRevenue and gsmsProfit
    // This prevents Contract Annual GP from being set to 0 when gsmsProfit is temporarily 0 during recalculation chain
    const existingGP = (newData.contractAnnualGP && newData.contractAnnualGP !== '0' && newData.contractAnnualGP !== '0.00' && parseFloat(newData.contractAnnualGP) !== 0)
      ? newData.contractAnnualGP
      : (updatedData.contractAnnualGP && updatedData.contractAnnualGP !== '0' && updatedData.contractAnnualGP !== '0.00' && parseFloat(updatedData.contractAnnualGP) !== 0)
        ? updatedData.contractAnnualGP
        : null;
    
    if (existingGP) {
      newData.contractAnnualGP = existingGP;
    } else {
      // If no valid existing value, calculate it using current contractAnnualRevenue and gsmsProfit
      // CRITICAL: Use the ORIGINAL gsmsProfit from updatedData (before recalculation) to avoid using 0
      // The gsmsProfit variable might be temporarily 0 during recalculation chain
      const currentRevenue = newData.contractAnnualRevenue || updatedData.contractAnnualRevenue;
      // Use updatedData.gsmsProfit first (original value before recalculation), then newData.gsmsProfit, then parsed gsmsProfit variable
      const originalGsmsProfit = parseFloat(updatedData.gsmsProfit) || parseFloat(newData.gsmsProfit) || (gsmsProfit !== undefined && !isNaN(gsmsProfit) ? gsmsProfit : 0);
      
      if (currentRevenue && currentRevenue !== '0' && currentRevenue !== '0.00' && parseFloat(currentRevenue) !== 0 && originalGsmsProfit !== 0 && !isNaN(originalGsmsProfit)) {
        const validRevenue = parseFloat(currentRevenue);
        const contractAnnualGPValue = F.calculateContractAnnualGP(validRevenue, originalGsmsProfit);
        newData.contractAnnualGP = safeToFixed(contractAnnualGPValue, 2);
      } else {
        // If revenue or profit is missing/0, remove the 0 value to prevent overwriting
        if (newData.contractAnnualGP === '0' || newData.contractAnnualGP === '0.00') {
          delete newData.contractAnnualGP;
        }
      }
    }
  } else if (changedField === 'gsmsProfit') {
    // When GSMS Profit changes, preserve the existing Contract Annual GP to keep Gross Profit unchanged
    // Gross Profit = (Contract Annual GP / Contract Annual Revenue) * 100 should remain constant
    // CRITICAL: If contractAnnualGP is 0 or empty string, recalculate it (don't preserve 0)
    const existingGP = newData.contractAnnualGP || updatedData.contractAnnualGP;
    const shouldRecalculateGP = existingGP === undefined || existingGP === null || existingGP === '' || existingGP === '0' || existingGP === '0.00' || parseFloat(existingGP) === 0;
    
    if (shouldRecalculateGP) {
      // Calculate Contract Annual GP when it doesn't exist or is 0
      const gsmsProfitForCalc = gsmsProfit !== undefined && !isNaN(gsmsProfit) ? gsmsProfit : 0;
      // Use contractAnnualRevenue from updatedData if available, otherwise use calculated revenue
      const validRevenue = updatedData.contractAnnualRevenue !== undefined && updatedData.contractAnnualRevenue !== null && updatedData.contractAnnualRevenue !== '' 
        ? parseFloat(updatedData.contractAnnualRevenue) 
        : (isNaN(revenue) ? 0 : revenue);
      const contractAnnualGPValue = F.calculateContractAnnualGP(validRevenue, gsmsProfitForCalc);
      newData.contractAnnualGP = safeToFixed(contractAnnualGPValue, 2);
    } else {
      // Preserve existing value
      newData.contractAnnualGP = existingGP;
    }
  }

  // Calculate Contract Annual Cost
  // Ensure gsmsCost and targetUsage are valid numbers before calculation
  const validGsmsCost = isNaN(gsmsCost) ? 0 : gsmsCost;
  const validTargetUsage = isNaN(targetUsage) ? 0 : targetUsage;
  const contractAnnualCostValue = F.calculateContractAnnualCost(validGsmsCost, validTargetUsage);
  newData.contractAnnualCost = safeToFixed(contractAnnualCostValue, 2);

  // NOTE: Blended WHLS Fee is now calculated earlier (before goal seek and GSMS Cost)
  // to ensure both use the updated value - see block after IFF Fee calculation

  // Calculate FSS Tracking fields (if Analysis Type is Price Change)
  if (newData.analysisType === 'Price Change' || analysisType === 'Price Change') {
    const bidPriceWithoutIff = F.calculateBidPriceWithoutIff(suggestedBidPrice);
    newData.bidPriceWithoutIff = bidPriceWithoutIff.toFixed(2);

    const bidPriceWithIff = F.calculateBidPriceWithIff(bidPriceWithoutIff);
    newData.bidPriceWithIff = bidPriceWithIff ? bidPriceWithIff.toFixed(2) : '';

    const awardedFssRatio = parseFloat(newData.awardedFssRatio) || null;
    if (awardedFssRatio) {
      const supplierComparableWac = wac; // Assuming comparable WAC is same as WAC
      
      const newTcPrice = F.calculateNewTcPrice(awardedFssRatio, supplierComparableWac, mfgBottleSize, packSize);
      newData.newTcPrice = newTcPrice ? newTcPrice.toFixed(2) : '';

      const proposedNewRatio = F.calculateProposedNewRatio(awardedFssRatio, bidPriceWithoutIff, supplierComparableWac, mfgBottleSize, packSize);
      newData.proposedNewRatio = proposedNewRatio ? proposedNewRatio.toFixed(2) : '';

      const maxPriceWithoutIff = F.calculateMaxPriceWithoutIff(awardedFssRatio, newTcPrice);
      newData.maxPriceWoutIff = maxPriceWithoutIff ? maxPriceWithoutIff.toFixed(2) : '';

      const maxPriceWithIff = F.calculateMaxPriceWithIff(awardedFssRatio, maxPriceWithoutIff);
      newData.maxPriceWithIff = maxPriceWithIff ? maxPriceWithIff.toFixed(2) : '';

      newData.ratioOk = F.checkRatioOk(awardedFssRatio, proposedNewRatio);
      newData.maxPriceOk = F.checkMaxPriceOk(awardedFssRatio, gsmsCost, maxPriceWithIff);
    }
  }

  // Calculate Government Savings Analysis (independent of TMOP %)
  // Formula: (Lowest Contract Price - Suggested Bid Price) * Target Annual Usage (MAS) or NC EST Annual Usage (NC)
  // This should be calculated whenever lowestContractPrice, suggestedBidPrice, or usage fields change
  const shouldRecalculateGovSavings = 
    !changedField || 
    changedField === 'lowestContractPrice' || 
    changedField === 'suggestedBidPrice' ||
    changedField === 'targetAnnualUsage' ||
    changedField === 'vaAnnualUsage' ||
    changedField === 'dodAnnualUsage' ||
    changedField === 'vaTmsPercent' ||
    changedField === 'dodTmsPercent' ||
    // NC-specific fields
    changedField === 'ncEstAnnualUsage' ||
    changedField === 'vaAnnualNcEst' ||
    changedField === 'dodAnnualNcEst' ||
    changedField === 'ihsAnnualNcEst' ||
    changedField === 'bopAnnualNcEst' ||
    (FIELD_DEPENDENCIES[changedField] && FIELD_DEPENDENCIES[changedField].includes('govSavingsAnalysis'));
  
  if (shouldRecalculateGovSavings) {
    // For NC template, use ncEstAnnualUsage; for MAS template, use targetAnnualUsage
    let usageForGovSavings = 0;
    if (isNC) {
      // NC template: use ncEstAnnualUsage
      usageForGovSavings = parseFloat(newData.ncEstAnnualUsage) || 0;
    } else {
      // MAS template: use targetUsage that was just calculated above, or get from newData if it exists
      usageForGovSavings = targetUsage > 0 ? targetUsage : (parseFloat(newData.targetAnnualUsage) || 0);
    }
    
    if (lowestContractPrice > 0 && suggestedBidPrice > 0 && usageForGovSavings > 0) {
      // Calculate Government Savings - doesn't require TMOP %
      const govtSavings = F.calculateGovernmentSavingsAnalysis(lowestContractPrice, suggestedBidPrice, usageForGovSavings);
      newData.govSavingsAnalysis = safeToFixed(govtSavings, 2);
    } else {
      // Only reset govSavingsAnalysis if we're recalculating due to relevant field changes
      if (isNC) {
        // For NC template, reset when NC-specific fields change
        if (changedField === 'lowestContractPrice' || changedField === 'suggestedBidPrice' || 
            changedField === 'vaAnnualNcEst' || changedField === 'dodAnnualNcEst' || 
            changedField === 'ihsAnnualNcEst' || changedField === 'bopAnnualNcEst' || 
            changedField === 'ncEstAnnualUsage') {
          newData.govSavingsAnalysis = '0.00';
        }
      } else {
        // For MAS template, reset when MAS-specific fields change
        if (changedField === 'lowestContractPrice' || changedField === 'suggestedBidPrice' || changedField === 'targetAnnualUsage' || 
            changedField === 'vaAnnualUsage' || changedField === 'dodAnnualUsage' || changedField === 'vaTmsPercent' || changedField === 'dodTmsPercent') {
          newData.govSavingsAnalysis = '0.00';
        }
      }
    }
  }

  // Calculate TMOP Savings (requires TMOP %)
  // lowestContractPrice is already extracted above from contract price ranking data
  // Always recalculate when tmopPercent, lowestContractPrice, or suggestedBidPrice changes
  // This ensures TMOP savings update when user changes TMOP percentage
  const shouldRecalculateTmopSavings = 
    !changedField || 
    changedField === 'tmopPercent' || 
    changedField === 'lowestContractPrice' || 
    changedField === 'suggestedBidPrice' ||
    changedField === 'vaAnnualUsage' ||
    changedField === 'dodAnnualUsage' ||
    (FIELD_DEPENDENCIES[changedField] && FIELD_DEPENDENCIES[changedField].includes('annualTmopSavings'));
  
  if (shouldRecalculateTmopSavings) {
    if (lowestContractPrice > 0 && suggestedBidPrice > 0 && tmopPercent > 0) {
      const annualTmopSavings = F.calculateAnnualTmopSavings(
        lowestContractPrice,
        suggestedBidPrice,
        vaUsage,
        dodUsage,
        tmopPercent
      );
      newData.annualTmopSavings = safeToFixed(annualTmopSavings, 2);

      const monthlyTmopSavings = F.calculateMonthlyTmopSavings(annualTmopSavings);
      newData.monthlyTmopSavings = safeToFixed(monthlyTmopSavings, 2);
    } else {
      // Set to 0 if required values are missing
      newData.annualTmopSavings = '0.00';
      newData.monthlyTmopSavings = '0.00';
    }
  }

  // CRITICAL FINAL CHECK: Ensure Contract Annual GP is always calculated correctly if we have revenue and profit
  // This ensures it's never left as 0 or undefined when it should have a value
  // CRITICAL: Use ORIGINAL gsmsProfit from updatedData (before recalculation) to avoid using wrong recalculated value
  const finalRevenue = newData.contractAnnualRevenue || updatedData.contractAnnualRevenue;
  // Use updatedData.gsmsProfit FIRST (original value before recalculation), then newData.gsmsProfit, then parsed variable
  // CRITICAL: Check if updatedData.gsmsProfit exists and is not null/undefined/empty BEFORE parsing
  // This ensures we use the correct gsmsProfit value (10), not a recalculated one that might be wrong (89.34)
  let originalGsmsProfit = 0;
  if (updatedData.gsmsProfit !== null && updatedData.gsmsProfit !== undefined && updatedData.gsmsProfit !== '') {
    const parsed = parseFloat(updatedData.gsmsProfit);
    if (!isNaN(parsed)) {
      originalGsmsProfit = parsed;
    }
  }
  // Only fall back to newData.gsmsProfit if updatedData.gsmsProfit was not available
  if (originalGsmsProfit === 0 && newData.gsmsProfit !== null && newData.gsmsProfit !== undefined && newData.gsmsProfit !== '') {
    const parsed = parseFloat(newData.gsmsProfit);
    if (!isNaN(parsed) && parsed !== 0) {
      originalGsmsProfit = parsed;
    }
  }
  // Last fallback to parsed variable
  if (originalGsmsProfit === 0 && gsmsProfit !== undefined && !isNaN(gsmsProfit) && gsmsProfit !== 0) {
    originalGsmsProfit = gsmsProfit;
  }
  
  if (finalRevenue && finalRevenue !== '0' && finalRevenue !== '0.00' && parseFloat(finalRevenue) !== 0 && 
      originalGsmsProfit !== 0 && !isNaN(originalGsmsProfit)) {
    const currentGP = parseFloat(newData.contractAnnualGP || '0');
    const expectedGP = F.calculateContractAnnualGP(parseFloat(finalRevenue), originalGsmsProfit);
    
    // If Contract Annual GP is 0, undefined, or doesn't match expected value, recalculate it
    if (currentGP === 0 || !newData.contractAnnualGP || Math.abs(currentGP - expectedGP) > 0.01) {
      newData.contractAnnualGP = safeToFixed(expectedGP, 2);
    }
  }
  
  return newData;
};
